import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { TextBoxData } from '@/lib/text/types/text-box-data'
import { mirrorPathCommands, pathCommandsToSvgPath } from '@/lib/elements/shape-path-commands'
import { resolveKindId } from '@/lib/elements/stowed-element-builder'
import { collectGroupMirrorMembers } from '@/lib/groups/mirror-group-expansion'
import { isGroupShape } from '@/lib/groups/group-kind'
import {
  isShapeExcludedFromUnifiedGeometryTransform,
  isShapeLockedForUserEdit,
  isTextExcludedFromUnifiedGeometryTransform,
  isTextLockedForUserEdit,
} from '@/lib/groups/element-edit-policy'
import { mirrorParagraphAlignment } from './helpers/mirror-text-across-spine'

/** kindId values Mirror Move/Copy currently support: rectangle, irregular polygon, regular polygon, pen path. */
export const MIRROR_ELIGIBLE_KIND_IDS: readonly number[] = [1, 2, 9, 10]

/**
 * Content-only check: kind support and visibility, with no group-membership gate — a group
 * descendant legitimately has `parentGroupId` set, so this is reused for both a flat root
 * selection (via `isShapeMirrorKindEligible` below) and for validating a group's descendants
 * (via `isGroupMirrorEligible`).
 *
 * Text (3), images (4), page numbers (5), and text-on-path (6) are out of scope for this pass —
 * see Phase 5 recipe follow-ups. Pen paths (9) are included so a hand-drawn shape carrying a
 * photo fill (see `fill-shape-with-photo.ts`'s `PHOTO_FILL_SUPPORTED_KIND_IDS`) isn't excluded
 * just because it's a pen path rather than a preset rectangle/polygon. Groups (7) have no content
 * of their own to mirror — see `isGroupMirrorEligible` for how a group's members are validated
 * instead of this allow-list.
 */
function isShapeMirrorContentEligible(shape: ShapeData): boolean {
  if (shape.hidden === true) return false
  // Freshly placed shapes (not yet round-tripped through a checkpoint save/load) have
  // no `kindId` of their own yet — resolve the kindId they'll be saved under instead.
  return MIRROR_ELIGIBLE_KIND_IDS.includes(resolveKindId(shape))
}

/** Checks shared by both Mirror Move and Mirror Copy eligibility for a flat, non-group root
 * selection: content eligibility plus "not itself a group member" (a shape inside a group must be
 * mirrored as part of that group, not selected+mirrored individually — see `isGroupMirrorEligible`
 * for that path). Lock/freeze are intentionally excluded here — the two callers below disagree
 * on frozen (see `isShapeMirrorEligible` vs `isShapeMirrorCopyEligible`). */
function isShapeMirrorKindEligible(shape: ShapeData): boolean {
  if (shape.parentGroupId != null) return false

  return isShapeMirrorContentEligible(shape)
}

/**
 * Eligibility for a selected group shape: all-or-nothing over every descendant, at any nesting
 * depth (through nested sub-groups) — one ineligible descendant blocks mirroring the whole group,
 * matching the existing `.every()` policy for a flat multi-selection. Partial mirroring would
 * leave the group's own box out of sync with its members' reflected positions with no clean undo
 * story. A nested group shell has no content of its own to mirror (just a box), so it's exempted
 * from the kind allow-list but still subject to the same hidden/lock/freeze checks as any leaf.
 */
function isGroupMirrorEligible(
  group: ShapeData,
  allShapes: ShapeData[],
  allTextBoxes: TextBoxData[],
  shapesById: Map<string, ShapeData>,
  mode: 'move' | 'copy'
): boolean {
  if (group.hidden === true) return false

  const { shapes, textBoxes } = collectGroupMirrorMembers(group, allShapes, allTextBoxes)
  const shapeLockCheck =
    mode === 'move' ? isShapeExcludedFromUnifiedGeometryTransform : isShapeLockedForUserEdit
  const textLockCheck =
    mode === 'move' ? isTextExcludedFromUnifiedGeometryTransform : isTextLockedForUserEdit

  const descendantShapesEligible = shapes.every((s) => {
    if (s.hidden === true) return false
    if (!isGroupShape(s) && !isShapeMirrorContentEligible(s)) return false

    return !shapeLockCheck(s, shapesById)
  })

  if (!descendantShapesEligible) return false

  return textBoxes.every((t) => t.hidden !== true && !textLockCheck(t, shapesById))
}

/**
 * Eligibility for Mirror Move — this repositions the shape itself, so it's blocked the same way
 * any other geometry gesture is: locked or frozen (`isShapeExcludedFromUnifiedGeometryTransform`).
 * `allTextBoxes` is only consulted when `shape` is a group, to validate its descendant text boxes.
 */
export function isShapeMirrorEligible(
  shape: ShapeData,
  shapesById: Map<string, ShapeData>,
  allTextBoxes: TextBoxData[] = []
): boolean {
  if (isGroupShape(shape)) {
    if (isShapeExcludedFromUnifiedGeometryTransform(shape, shapesById)) return false

    return isGroupMirrorEligible(
      shape,
      Array.from(shapesById.values()),
      allTextBoxes,
      shapesById,
      'move'
    )
  }

  if (!isShapeMirrorKindEligible(shape)) return false

  return !isShapeExcludedFromUnifiedGeometryTransform(shape, shapesById)
}

/**
 * Eligibility for Mirror Copy — this creates a brand-new, independent shape and never touches
 * the original's geometry, so a frozen source is fine (freeze only blocks changing the existing
 * object's placement, not creating an unrelated one) — matching how regular copy/paste already
 * treats frozen shapes (`canUseClipboardEntry` in `center-canvas/hooks/use-clipboard-commands.ts` only rejects locked). Locked
 * still blocks it, same as copy/paste. `allTextBoxes` is only consulted when `shape` is a group.
 */
export function isShapeMirrorCopyEligible(
  shape: ShapeData,
  shapesById: Map<string, ShapeData>,
  allTextBoxes: TextBoxData[] = []
): boolean {
  if (isGroupShape(shape)) {
    if (isShapeLockedForUserEdit(shape, shapesById)) return false

    return isGroupMirrorEligible(
      shape,
      Array.from(shapesById.values()),
      allTextBoxes,
      shapesById,
      'copy'
    )
  }

  if (!isShapeMirrorKindEligible(shape)) return false

  return !isShapeLockedForUserEdit(shape, shapesById)
}

/**
 * Reflects a shape across the vertical line `x = axisX` (the spread's spine, in
 * canvas doc-px). Reflection anti-commutes with rotation, so rotation is negated;
 * `pathCommands` (local shape-space vertices) are re-mirrored via the same helper
 * used for shape-tool drag-create mirroring.
 */
export function mirrorShapeAcrossVerticalAxis(shape: ShapeData, axisX: number): Partial<ShapeData> {
  const mirrored: Partial<ShapeData> = {
    x: 2 * axisX - shape.x - shape.width,
    rotation: (360 - (shape.rotation ?? 0)) % 360,
  }

  if (shape.pathCommands && shape.pathCommands.length > 0) {
    mirrored.pathCommands = mirrorPathCommands(
      shape.pathCommands,
      { x: true, y: false },
      { width: shape.width, height: shape.height }
    )

    // Pen-drawn shapes render from `shape.path` (raw SVG string) when present, taking priority
    // over `pathCommands` (see the pen-path branch in `shape-renderer/helpers/shape-path-resolution.ts`) — regenerate it from
    // the mirrored commands or the mirror would be computed but never actually drawn.
    if (typeof shape.path === 'string' && shape.path.trim().length > 0) {
      const isClosed = /[Zz]\s*$/.test(shape.path.trim())

      mirrored.path = pathCommandsToSvgPath(mirrored.pathCommands, { close: isClosed })
    }
  }

  // A photo fill's pan is a delta from the frame's own center, so it negates the same way an
  // x-coordinate does under reflection; the image content itself flips independently (rendered
  // via `photoFillImageTransform`, persisted as legacy's own negative-width convention — see
  // `buildShapeImageFillContent`). Both together reproduce a true mirror of the photo's on-screen
  // appearance, not just its position.
  if (shape.photoFill) {
    mirrored.photoFill = {
      ...shape.photoFill,
      offsetX: -shape.photoFill.offsetX,
      flippedX: !shape.photoFill.flippedX,
    }
  }

  // Text typed inside the shape (see `ShapeData.paragraphs`, rendered by
  // `ShapeTextEditor`) flips left/right the same way a standalone text box's paragraphs do —
  // see `mirrorParagraphAlignment`.
  if (shape.paragraphs && shape.paragraphs.length > 0) {
    mirrored.paragraphs = shape.paragraphs.map((p) => ({
      ...p,
      alignment: mirrorParagraphAlignment(p.alignment),
    }))
  }

  return mirrored
}

/**
 * Builds the (not-yet-inserted) `ShapeData` for a Mirror Copy: the original's styling
 * and geometry, reflected across `axisX`, with `id`/`fractionalIndex` cleared so the
 * caller's create path (`ShapeManagerRef.addShapeData`) assigns fresh ones, and `uid`
 * cleared so the copy doesn't inherit the original's text-linking chain identity
 * (see `ShapeData.uid`) — a shape carrying `paragraphs` is a chain member (WMPCM-7988),
 * and chains reference members by uid, not id. This is the only Mirror Copy builder now;
 * its `buildMirrorCopyTextBoxData` counterpart went with `mirror-text-across-spine.ts`'s
 * text half in text-unification Phase 4.
 */
export function buildMirrorCopyShapeData(shape: ShapeData, axisX: number): ShapeData {
  return {
    ...shape,
    ...mirrorShapeAcrossVerticalAxis(shape, axisX),
    id: '',
    fractionalIndex: undefined,
    uid: undefined,
  }
}
